<script setup lang="ts">
import { trackEvent } from '@@/bits/analytics'
import device from '@@/bits/device'
import { emojiUrl } from '@@/bits/emoji'
import { observeElementIntersection } from '@@/bits/intersection'
import { __, np__ } from '@@/bits/intl'
import type {
  AiImageSearchResult,
  AudioSearchResult,
  ImageSearchResult,
  VideoSearchResult,
  WebSearchResult,
} from '@@/bits/web_search'
import { SearchFilters } from '@@/bits/web_search'
import OzContainedButton, {
  OzContainedButtonColorScheme,
  OzContainedButtonSizePreset,
} from '@@/library/v4/components/OzContainedButton.vue'
import OzIndeterminateSpinner from '@@/library/v4/components/OzIndeterminateSpinner.vue'
import { useDarkModeStore } from '@@/pinia/dark_mode'
import ContentPickerSearchResultsAv from '@@/vuecomponents/ContentPickerSearchResultsAv.vue'
import ContentPickerSearchResultsImage from '@@/vuecomponents/ContentPickerSearchResultsImage.vue'
import ContentPickerSearchResultsWeb from '@@/vuecomponents/content-picker-search-results-web.vue'
import WebBrowserAttribution from '@@/vuecomponents/web_browser_attribution.vue'
import { processedUrl } from '@padlet/vivaldi-client'
import { storeToRefs } from 'pinia'
import { nextTick, onUnmounted, ref, watch } from 'vue'

const props = defineProps<{
  isOnline: boolean
  searchQuery: string
  searchFilter: SearchFilters | null
  results: (AudioSearchResult | WebSearchResult | VideoSearchResult | ImageSearchResult | AiImageSearchResult)[]
  isSearchQueryInProgress: boolean
  isSearchMoreQueryInProgress: boolean
  isAiImageSearchMode: boolean
  canSearchMore: boolean
  hasError: boolean
}>()

const emit = defineEmits<{
  (name: 'select', data: string): void
  (name: 'search-more'): void
  (name: 'clear-search-error'): void
}>()

// From JoyPixels
const EMOJIS = {
  '1f626': {
    code: '1f626',
    category: 'people',
    shortname: ':frowning:',
    name: 'frowning face with open mouth',
    keywords: ['face', 'frown', 'mouth', 'open'],
    url: emojiUrl('png', '1f626'),
  },
  '1f633': {
    code: '1f633',
    category: 'people',
    shortname: ':flushed:',
    name: 'flushed face',
    keywords: ['dazed', 'face', 'flushed'],
    url: emojiUrl('png', '1f633'),
  },
  '1f634': {
    code: '1f634',
    category: 'people',
    shortname: ':sleeping:',
    name: 'sleeping face',
    keywords: ['face', 'sleep', 'zzz'],
    url: emojiUrl('png', '1f634'),
  },
}

const isMobile = device.mobile
const { isDarkMode } = storeToRefs(useDarkModeStore())

const resultBody = ref<HTMLDivElement | null>(null)

const hasNoResults = ref<boolean>(false)
const intersectionObserver = ref<IntersectionObserver | null>(null)

const processedIconUrl = (icon: object & { url: string }): string => {
  return processedUrl(icon.url, { width: 64, height: 64, preset: 'icon' })
}

const select = (data: string): void => {
  if (props.isAiImageSearchMode) {
    trackEvent('AI Image Search', 'Selected image from AI search')
  }
  emit('select', data)
}

watch(
  () => props.results,
  (): void => {
    nextTick(() => {
      // necessary to wait for the element to appear
      const searchMoreTrigger = document.querySelector('#search-more-trigger') as HTMLDivElement
      if (searchMoreTrigger) {
        intersectionObserver.value = observeElementIntersection(searchMoreTrigger, () => {
          emit('search-more')
        })
      }
    })
  },
)

watch([() => props.isSearchQueryInProgress, () => props.searchFilter], (): void => {
  let xResults = true
  if (props.searchQuery) {
    xResults = props.results && props.results.length > 0
  }
  hasNoResults.value = !xResults
})

watch(
  () => props.searchFilter,
  (): void => {
    if (!resultBody.value) return
    resultBody.value.scrollTop = 0
  },
)

watch(
  () => props.isOnline,
  (): void => {
    emit('clear-search-error')
  },
)

onUnmounted(() => {
  const searchMoreTrigger = document.querySelector('#search-more-trigger') as HTMLDivElement
  if (intersectionObserver.value !== null && searchMoreTrigger) {
    intersectionObserver.value.unobserve(searchMoreTrigger)
  }
})
</script>

<template>
  <div
    :class="[
      'relative flex grow flex-col overflow-hidden',
      isAiImageSearchMode && !isMobile ? 'h-auto pb-2' : 'h-full',
    ]"
    role="status"
  >
    <div v-if="isSearchQueryInProgress" :class="[!isAiImageSearchMode && 'mt-2']">
      <div
        v-if="isAiImageSearchMode"
        class="h-109 my-0.5 text-dark-text-100 dark:text-light-text-100 flex flex-col items-center justify-center pb-8"
      >
        <div class="pb-4"><OzIndeterminateSpinner :size="48" /></div>
        <p class="text-input-body-small">
          <strong>{{ __('Drawing your words...') }}</strong>
        </p>
        <p class="text-body-extra-small">{{ __('This may take up to a minute') }}</p>
      </div>
      <OzIndeterminateSpinner v-else class="mx-auto" :size="32" />
    </div>
    <div
      v-else-if="isAiImageSearchMode && hasError && isOnline"
      class="flex grow justify-center items-center flex-col text-highway-100 dark:text-white-100 h-full min-h-1/4 mb-8 px-4"
    >
      <p class="pb-4">
        <img width="48" height="48" :src="processedIconUrl(EMOJIS['1f633'])" :alt="EMOJIS['1f634'].name" />
      </p>
      <p class="text-center">
        {{ __("Sorry! Looks like we can't draw that either.") }}
      </p>
    </div>
    <div
      v-else-if="hasNoResults && isOnline"
      class="flex grow justify-center items-center flex-col text-highway-100 dark:text-white-100 h-full min-h-1/4 mb-8 px-4"
    >
      <p class="pb-4">
        <img width="48" height="48" :src="processedIconUrl(EMOJIS['1f626'])" :alt="EMOJIS['1f634'].name" />
      </p>
      <p class="text-center">{{ __('No results found for your search term.') }}</p>
      <p class="text-center">{{ __('Hope you find what you are looking for!') }}</p>
    </div>
    <div
      v-else-if="results"
      ref="resultBody"
      :class="['overflow-auto', 'px-4', 'scrollbar-regular', 'dark:scrollbar-regular-dark']"
    >
      <p class="sr-only">
        {{
          np__('Number of search results found.', '{count} search result', '{count} search results', results.length, {
            count: results.length,
          })
        }}
      </p>
      <div v-if="searchFilter === SearchFilters.Gif" class="flex flex-1 items-center justify-center" role="note">
        <WebBrowserAttribution
          :aria-label="__('Powered By GIPHY')"
          :provider="isDarkMode ? 'giphyWhite' : 'giphyBlack'"
        />
      </div>
      <ContentPickerSearchResultsWeb v-if="searchFilter == SearchFilters.Web" :results="results" @select="select" />
      <ContentPickerSearchResultsImage
        v-else-if="searchFilter === SearchFilters.Images"
        :filter="searchFilter"
        :results="results"
        @select="select"
      />
      <ContentPickerSearchResultsImage
        v-else-if="searchFilter === SearchFilters.UnsplashImages"
        :filter="searchFilter"
        :results="results"
        @select="select"
      />
      <ContentPickerSearchResultsImage
        v-else-if="searchFilter === SearchFilters.AiImages"
        :filter="searchFilter"
        :results="results"
        @select="select"
      />
      <ContentPickerSearchResultsImage
        v-else-if="searchFilter === SearchFilters.Gif"
        :filter="searchFilter"
        :results="results"
        @select="select"
      />
      <ContentPickerSearchResultsImage
        v-else-if="searchFilter === SearchFilters.Stock"
        :filter="searchFilter"
        :results="results"
        @select="select"
      />
      <ContentPickerSearchResultsAv
        v-else-if="searchFilter === SearchFilters.Audio"
        :filter="searchFilter"
        :results="results"
        @select="select"
      />
      <ContentPickerSearchResultsAv
        v-else-if="searchFilter === SearchFilters.Videos"
        :filter="searchFilter"
        :results="results"
        @select="select"
      />
      <transition name="fade">
        <div v-if="isSearchMoreQueryInProgress" class="absolute bottom-2 inset-x-0 my-2 flex justify-center">
          <div class="bg-modal-light dark:bg-dark-ui-300 p-2 rounded-xl shadow-xl animate-bounce">
            <OzIndeterminateSpinner :size="24" />
          </div>
        </div>
      </transition>
      <div v-if="canSearchMore && isOnline" id="search-more-trigger" class="flex justify-center mt-4 mb-8 h-12">
        <OzContainedButton
          v-show="!isSearchMoreQueryInProgress"
          :color-scheme="OzContainedButtonColorScheme.Secondary"
          :size-preset="OzContainedButtonSizePreset.H32px"
          class="relative mx-auto mt-4"
          :text="__('Load more')"
          @click="$emit('search-more')"
        />
      </div>
    </div>
    <transition name="fade">
      <div
        v-if="!isSearchQueryInProgress && !hasNoResults && hasError && isOnline"
        class="flex grow justify-center items-center flex-col text-highway-100 dark:text-white-100 h-full min-h-1/4 mb-8 px-4"
      >
        <p class="pb-4">
          <img width="48" height="48" :src="processedIconUrl(EMOJIS['1f633'])" :alt="EMOJIS['1f634'].name" />
        </p>
        <p class="text-center">{{ __('Oops! Something went wrong.') }}</p>
        <p class="text-center">{{ __('Please try again.') }}</p>
      </div>
    </transition>
    <transition name="fade">
      <!-- N.B. If not online, we cannot load image, therefore
      we use v-show instead of v-if to preload this part -->
      <div
        v-show="!isSearchQueryInProgress && !isOnline"
        class="flex grow justify-center items-center flex-col text-highway-100 dark:text-white-100 h-full min-h-1/4 mb-8 px-4"
      >
        <p class="pb-4">
          <img width="48" height="48" :src="processedIconUrl(EMOJIS['1f634'])" :alt="EMOJIS['1f634'].name" />
        </p>
        <p class="text-center">{{ __("You're offline.") }}</p>
        <p class="text-center">{{ __('Please try again.') }}</p>
      </div>
    </transition>
  </div>
</template>

<style lang="scss" scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.25s;
}
.fade-enter,
.fade-leave-to {
  transition: none;
}
</style>
